import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import {
  columnFloorVariableName,
  columnWidthVariableName,
} from '~components/DataTable/dataTableColumnVariables';

// Match MUI DataGrid's cell padding and minimum column width, so migrated pages keep
// their density.
export const CELL_HORIZONTAL_PADDING_PX = 10;
const DEFAULT_MIN_COLUMN_WIDTH_PX = 50;
// Keeps one long free-text value from pushing every other column off-screen. Users
// can still drag a column wider than this.
const FITTED_MAX_WIDTH_PX = 400;

const CONTENT_PADDING_PX = CELL_HORIZONTAL_PADDING_PX * 2;

type SizedColumn = Pick<
  DataTableColumn<never>,
  'field' | 'maxWidth' | 'minWidth' | 'pin' | 'resizable'
>;

// The browser sizes every column to its content under `table-layout: auto` and hands
// the row's leftover width to the columns without a set width. Pinned and `maxWidth`
// columns ask for 1%, which keeps them at their content width.
//
// The content's `min-width` does the rest, because automatic layout treats a cell's own
// width as a preference only. A dragged width holds the column at exactly that width.
// Otherwise the column's floor, the widest its content has been on any page so far,
// stops it narrowing when the next page has shorter values.
export default function getColumnSlots(column: SizedColumn) {
  const widthVariableName = columnWidthVariableName(column.field);
  const floorVariableName = columnFloorVariableName(column.field);
  const keepsFittedWidth = Boolean(column.pin) || column.maxWidth !== undefined;
  const fittedMaxWidth = column.maxWidth ?? FITTED_MAX_WIDTH_PX;
  const minWidth = column.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH_PX;
  const undraggedWidth = keepsFittedWidth ? '1%' : 'auto';
  const undraggedMinWidth = `max(${minWidth}px, var(${floorVariableName}, 0px))`;

  return {
    cellSx: {
      width: `var(${widthVariableName}, ${undraggedWidth})`,
    },
    contentSx: {
      maxWidth: `calc(var(${widthVariableName}, ${fittedMaxWidth}px) - ${CONTENT_PADDING_PX}px)`,
      minWidth: `calc(var(${widthVariableName}, ${undraggedMinWidth}) - ${CONTENT_PADDING_PX}px)`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    fittedMaxWidth,
    isResizable: column.resizable ?? true,
    minWidth,
  };
}
